'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ClinicalNoteState,
  StreamClinicalNoteRequest,
  StreamClinicalNoteResponse,
} from '@/types/clinical-note';

interface UseStreamClinicalNoteOptions {
  sessionId?: string; // Session ID for auto-saving
  onComplete?: (note: string) => void;
  onError?: (error: string) => void;
  onProgress?: (progress: number) => void;
}

export function useStreamClinicalNote(options: UseStreamClinicalNoteOptions = {}) {
  const [state, setState] = useState<ClinicalNoteState>({
    originalNote: '',
    draftNote: '',
    isStreaming: false,
    error: null,
    hasChanges: false,
    streamingProgress: 0,
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  // Use ref to store the latest sessionId to avoid stale closure issues
  const sessionIdRef = useRef<string | undefined>(options.sessionId);

  // Update ref whenever sessionId changes
  useEffect(() => {
    sessionIdRef.current = options.sessionId;
  }, [options.sessionId]);

  const streamClinicalNote = useCallback(
    async (request: StreamClinicalNoteRequest) => {
      // Cleanup previous stream
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      setState((prev) => ({
        ...prev,
        isStreaming: true,
        error: null,
        streamingProgress: 0,
        originalNote: '',
        draftNote: '',
        hasChanges: false,
      }));

      try {
        if (request.sessionId) {
          const enqueueResponse = await fetch(
            `/api/sessions/${encodeURIComponent(request.sessionId)}/clinical-note-generations`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              signal: abortControllerRef.current.signal,
            }
          );
          if (!enqueueResponse.ok) {
            throw new Error(`HTTP ${enqueueResponse.status}: clinical-note enqueue failed`);
          }
          const queued = (await enqueueResponse.json()) as { run_id: string; status: string };
          const deadline = Date.now() + 180_000;
          let progress = queued.status === 'succeeded' ? 100 : 10;
          while (Date.now() < deadline) {
            const statusResponse = await fetch(
              `/api/clinical-note-generations/${encodeURIComponent(queued.run_id)}`,
              { cache: 'no-store', signal: abortControllerRef.current.signal }
            );
            if (!statusResponse.ok) {
              throw new Error(`HTTP ${statusResponse.status}: clinical-note status failed`);
            }
            const generation = (await statusResponse.json()) as {
              status: 'queued' | 'running' | 'succeeded' | 'failed';
              clinical_note?: string | null;
              error_code?: string | null;
            };
            if (generation.status === 'succeeded' && generation.clinical_note) {
              const note = generation.clinical_note;
              setState((previous) => ({
                ...previous,
                originalNote: note,
                draftNote: note,
                isStreaming: false,
                streamingProgress: 100,
                hasChanges: false,
              }));
              options.onProgress?.(100);
              options.onComplete?.(note);
              return;
            }
            if (generation.status === 'failed') {
              throw new Error(generation.error_code || 'Clinical-note generation failed');
            }
            progress = Math.min(progress + (generation.status === 'running' ? 8 : 2), 90);
            setState((previous) => ({ ...previous, streamingProgress: progress }));
            options.onProgress?.(progress);
            await new Promise((resolve) => window.setTimeout(resolve, 1000));
          }
          throw new Error('Clinical-note generation timed out');
        }

        const response = await fetch('/api/summarize', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(request),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        if (!response.body) {
          throw new Error('No response body received');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let accumulatedText = '';

        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          const chunk = decoder.decode(value, { stream: true });

          // Handle both raw text chunks and JSON-formatted chunks
          // A single read() may contain multiple JSON lines, so split by newline
          const lines = chunk.split('\n').filter((line) => line.trim());

          for (const line of lines) {
            try {
              // Try to parse as JSON first (for structured streaming)
              const parsedChunk: StreamClinicalNoteResponse = JSON.parse(line);

              if (parsedChunk.type === 'error') {
                throw new Error(parsedChunk.error || 'Stream error');
              }

              if (parsedChunk.type === 'chunk' && parsedChunk.data) {
                accumulatedText += parsedChunk.data;
              }

              if (parsedChunk.metadata?.progress) {
                options.onProgress?.(parsedChunk.metadata.progress);
                setState((prev) => ({
                  ...prev,
                  streamingProgress: parsedChunk.metadata!.progress!,
                }));
              }
            } catch {
              // Fallback: treat as raw text chunk
              accumulatedText += line;
            }
          }

          // Update state with accumulated text
          setState((prev) => ({
            ...prev,
            originalNote: accumulatedText,
            draftNote: accumulatedText,
          }));
        }

        // Mark streaming as complete
        setState((prev) => ({
          ...prev,
          isStreaming: false,
          streamingProgress: 100,
        }));

        // Auto-save clinical note if sessionId is provided
        // Use ref to get the latest sessionId value
        const currentSessionId = sessionIdRef.current;

        console.log('🔍 Auto-save check:', {
          hasSessionId: !!currentSessionId,
          sessionId: currentSessionId,
          hasText: !!accumulatedText.trim(),
          textLength: accumulatedText.length,
        });

        if (currentSessionId && accumulatedText.trim()) {
          try {
            console.log(
              `📝 Attempting to auto-save clinical note for session: ${currentSessionId}`
            );
            const saveResponse = await fetch(`/api/sessions/${currentSessionId}/save-note`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ note_content: accumulatedText }),
            });

            if (saveResponse.ok) {
              console.log('✅ Clinical note auto-saved successfully');
            } else {
              console.warn('⚠️  Failed to auto-save clinical note:', saveResponse.status);
            }
          } catch (saveError) {
            console.error('❌ Error auto-saving clinical note:', saveError);
          }
        } else {
          console.log('⏭️  Skipping auto-save:', {
            reason: !currentSessionId ? 'No sessionId' : 'No text content',
          });
        }

        options.onComplete?.(accumulatedText);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown streaming error';

        setState((prev) => ({
          ...prev,
          isStreaming: false,
          error: errorMessage,
          streamingProgress: 0,
        }));

        options.onError?.(errorMessage);
      }
    },
    [options]
  );

  const updateDraftNote = useCallback((newNote: string) => {
    setState((prev) => ({
      ...prev,
      draftNote: newNote,
      hasChanges: newNote !== prev.originalNote,
    }));
  }, []);

  const resetNote = useCallback(() => {
    setState((prev) => ({
      ...prev,
      draftNote: prev.originalNote,
      hasChanges: false,
    }));
  }, []);

  const cancelStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setState((prev) => ({
      ...prev,
      isStreaming: false,
      error: null,
    }));
  }, []);

  return {
    ...state,
    streamClinicalNote,
    updateDraftNote,
    resetNote,
    cancelStream,
  };
}
